import { z } from 'zod'

import { nanoidSchema } from '@scalar-org/entities/utility'

import type { RequestService } from '../request'

export function publishApiFactory(requestService: RequestService) {
  /** Publish CLI */
  async function publishCli({
    tarStream,
    slug,
    configPath,
    preview,
  }: {
    tarStream: ReadableStream
    slug: string
    configPath: string
    preview?: boolean
  }) {
    const params = new URLSearchParams({
      slug,
      configPath,
      ...(preview ? { preview: 'true' } : {}),
    })

    return await requestService.request({
      url: `publish-deploy/publish/cli?${params.toString()}`,
      method: 'post',
      data: tarStream,
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      schema: z.object({ publishUid: nanoidSchema }),
    })
  }

  /** Publish GitHub */
  async function publishGithub({
    slug,
    configPath,
    preview,
    commitSha,
  }: {
    slug: string
    configPath?: string
    preview?: boolean
    commitSha?: string
  }) {
    return await requestService.request({
      url: 'publish-deploy/publish/github',
      method: 'post',
      data: {
        slug,
        configPath,
        preview,
        commitSha,
      },
      schema: z.object({ publishUid: nanoidSchema }),
    })
  }

  /** Publish WYSIWYG */
  async function publishWysiwyg(uid: string) {
    return await requestService.request({
      url: 'publish-deploy/publish/wysiwyg',
      method: 'post',
      data: {
        uid,
      },
      schema: z.object({
        publishUid: nanoidSchema,
      }),
    })
  }

  /** Check DNS record setup for a domain */
  async function checkDNSRecords(domain: string) {
    return await requestService.request({
      url: 'core/publish/dns-check',
      method: 'post',
      data: {
        domain,
      },
      schema: z.object({
        invalidRecords: z.boolean(),
      }),
    })
  }

  /** Unpublish a deployed site */
  async function unpublish(projectUid?: string, githubProjectUid?: string) {
    return await requestService.request({
      url: 'core/unpublish',
      method: 'delete',
      data: {
        projectUid,
        githubProjectUid,
      },
      schema: z.null(),
    })
  }

  /** Re-deploy a publish record */
  async function redeploy(publishUid: string) {
    return await requestService.request({
      url: 'core/publish/redeploy',
      method: 'post',
      data: {
        publishUid,
      },
      schema: z.null(),
    })
  }

  /** Delete a deploy record */
  async function deleteRecord(publishUid: string) {
    return await requestService.request({
      url: 'core/publish',
      method: 'delete',
      data: {
        publishUid,
      },
      schema: z.null(),
    })
  }

  async function bulkDelete(projectUid: string) {
    return await requestService.request({
      url: 'core/publish/bulk',
      method: 'delete',
      data: {
        projectUid,
      },
      schema: z.null(),
    })
  }

  async function getPublishLogs(publishUid: string) {
    return await requestService.request({
      url: `core/publish/logs/${publishUid}`,
      method: 'get',
      schema: z.string(),
      stream: true,
    })
  }

  async function getDomainAvailability(domain: string) {
    return await requestService.request({
      url: 'core/publish/domains/availability',
      method: 'post',
      data: {
        domain,
      },
      schema: z.object({
        available: z.boolean(),
      }),
    })
  }

  async function getProjectDomains(projectUid: string) {
    return await requestService.request({
      url: 'core/publish/domains/project',
      method: 'post',
      data: {
        projectUid,
      },
      schema: z.object({
        scalarDomain: z.string().nullable(),
        customDomain: z.string().nullable(),
      }),
    })
  }

  /** Export a WYSIWYG project as a ZIP bundle and trigger a browser download */
  async function exportWysiwyg(
    uid: string,
    filename?: string,
  ): Promise<
    | {
        readonly error: true
        readonly message: string
        readonly status: number
      }
    | { readonly error: false }
  > {
    const token = requestService.getAuthToken?.()
    const url =
      `${requestService.baseUrl}/publish-deploy/export/wysiwyg`.replace(
        /([^:]\/)\/+/g,
        '$1',
      )

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ uid }),
    })

    if (!response.ok) {
      const text = await response.text().catch(() => 'Export failed')
      return { error: true, message: text, status: response.status } as const
    }

    const blob = await response.blob()
    const downloadUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename ?? 'project-export.zip'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(downloadUrl)

    return { error: false } as const
  }

  return {
    publishCli,
    publishGithub,
    publishWysiwyg,
    exportWysiwyg,
    checkDNSRecords,
    unpublish,
    deleteRecord,
    redeploy,
    bulkDelete,
    getPublishLogs,
    getDomainAvailability,
    getProjectDomains,
  }
}
